/**
 * The Cockpit's hero — brief part 1 §6 band 1 and §7.2 (DEC-87, DEC-95).
 *
 * 150px high: a black-and-white photograph drawn at random (the same pool as the sign-in screen,
 * ADR 9018), four vertical rules in `hero-rule` (white at 14%), and NO caption — the designer
 * confirmed the caption for the sign-in photograph and ruled it out here (§7.2). Above the title, a
 * kicker: the greeting, the market of the run on screen and the planner's ISO week. The title, in
 * the display face at 34px, is the page's synthesis sentence (`cockpitSynthesis`) — «7 decisioni
 * aspettano te, per 84 M COP» — built from the reads the page makes, never from the designer's
 * example values.
 *
 * Nothing measured sits on the photograph but that sentence, whose figures are the "Needs a
 * decision" table's own, directly below. The ink veil keeps white text readable on any photograph.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isoWeek } from '@/lib/format';

import { useAuthSession } from '../../../auth/session';
import { BrandHero, pickHeroPhotograph } from '../../chrome/BrandHero';

/** Which greeting the local hour calls for. Exported so the boundaries are tested, not eyed. */
export function partOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * A market's name in the planner's language — `CO` → "Colombia" — or the key itself when the
 * runtime does not name it. The key is data (G5); this only spells it.
 */
export function marketName(countryKey: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region', fallback: 'code' }).of(countryKey) ?? countryKey;
  } catch {
    return countryKey;
  }
}

export interface CockpitHeroProps {
  /** The market of the run on screen; `null` while none is known — the kicker then names none. */
  readonly country: string | null;
  /** The synthesis sentence, as a catalogue key and its arguments (`cockpitSynthesis`). */
  readonly title: { readonly key: string; readonly values: Readonly<Record<string, string | number>> };
}

export function CockpitHero({ country, title }: CockpitHeroProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { givenName, isAuthenticated } = useAuthSession();
  // Chosen once per mount, so the photograph does not change while the planner reads.
  const [photograph] = useState(() => pickHeroPhotograph());
  const now = new Date();
  const part = partOfDay(now.getHours());
  // The given name or nobody — never the login (audit v5 N-07). "GOOD AFTERNOON, E2E-FIXTURE"
  // set a username in display capitals, where it read as a code; `mrossi01` or an email would
  // have been worse. A greeting with no name is still a greeting.
  const name = isAuthenticated && givenName !== null ? givenName : undefined;
  const greeting = name === undefined ? t(`overview.greetingPlain.${part}`) : t(`overview.greeting.${part}`, { name });

  return (
    <div className="relative flex h-[150px] items-end overflow-hidden" data-cockpit-hero="">
      <BrandHero variant="band" photograph={photograph} className="absolute inset-0" />
      {/* The ink veil — the rail's own ink, so white text reads on any photograph. */}
      <div aria-hidden className="bg-rail-veil absolute inset-0" />
      {/* The brand's four columns (§7.2): a rule at the inline start of each, in `hero-rule`. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid grid-cols-4" data-hero-rules="">
        <span className="border-hero-rule border-s" />
        <span className="border-hero-rule border-s" />
        <span className="border-hero-rule border-s" />
        <span className="border-hero-rule border-s" />
      </div>
      <div className="relative flex min-w-0 flex-col gap-1 px-page pb-[20px]">
        <p className="text-hero-caption truncate text-[11px] uppercase tracking-[0.12em]" data-hero-kicker="">
          {t('overview.hero.kicker', {
            greeting,
            market: country === null ? 'none' : marketName(country, i18n.language),
            week: isoWeek(now).week,
          })}
        </p>
        <h1 className="text-rail-fg font-display text-[34px] font-bold uppercase leading-none">
          {t(title.key, title.values)}
        </h1>
      </div>
    </div>
  );
}
